import React from 'react'
import { useQuery } from '@apollo/client'
import { Config } from '@chegg-wt/core/config'
import { GetPapers } from '@chegg-wt/core/queries/GetPapers'
import { GET_PAPERS } from '@chegg-wt/core/queries/Papers'
import { connect } from 'react-redux'
import { AppState } from '@chegg-wt/core/state/types'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { Dispatch } from 'redux'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { LoadingDots } from '@chegg-wt/core/components/LoadingDots'
import {
  PAPER_LIST,
  SEE_ALL_PAPERS,
} from '@chegg-wt/core/components/Dashboard/utils/testIds'
import { loggedInSelector } from '@chegg-wt/core/state/user/selectors'
import { ListContainer, List, SeeAllLink } from '../../BaseProjectList/styled'
import PaperListItem from './PaperListItem'

export interface PapersListDispatchProps {
  onUserClick: () => void
}

export interface PapersListStateProps {
  isLoggedIn: boolean
}

export type PapersListProps = PapersListStateProps & PapersListDispatchProps

export const MAX_PAPER_ITEMS = 4

export const seeAllHref = `${Config.writingUrl}`

const PapersList: React.FC<PapersListProps> = ({ isLoggedIn, onUserClick }) => {
  const skipRequest = !isLoggedIn
  const { data, loading, error } = useQuery<GetPapers>(GET_PAPERS, {
    variables: { limit: MAX_PAPER_ITEMS },
    ssr: false,
    skip: skipRequest,
    fetchPolicy: 'cache-and-network',
  })

  if (loading && !skipRequest) {
    return (
      <ListContainer loadingContainer>
        <LoadingDots message="Loading your papers" />
      </ListContainer>
    )
  }
  if (error || !data || !data.getPapers || !data.getPapers.length) {
    return null
  }

  const handleClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    onUserClick()
    window.location.href = seeAllHref
  }

  // Tupac pagination not officially supported yet
  const paperItems = data.getPapers.slice(0, MAX_PAPER_ITEMS).map((paper, index) => {
    const { uploadUuid, updatedAt, filename } = paper
    return (
      <li key={uploadUuid}>
        <PaperListItem
          uploadUuid={uploadUuid}
          name={filename}
          date={updatedAt}
          index={index}
        />
      </li>
    )
  })

  const hasMaxOrMoreItems = paperItems.length >= MAX_PAPER_ITEMS

  return (
    <ListContainer data-test={PAPER_LIST}>
      <List>{paperItems}</List>
      {hasMaxOrMoreItems && (
        <SeeAllLink
          aria-label="See all papers"
          data-test={SEE_ALL_PAPERS}
          onClick={handleClick}
          href={seeAllHref}
        />
      )}
    </ListContainer>
  )
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onUserClick: () => dispatch(userClick({ origin: origins.dashboardClickPaperSeeAll })),
})

const mapStateToProps = (state: AppState): PapersListStateProps => ({
  isLoggedIn: loggedInSelector(state),
})
export default connect<PapersListStateProps, PapersListDispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(PapersList)
